import { useState, useMemo } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { useArticlesDB } from "@/hooks/useArticlesDB";
import { useAdminI18n } from "@/hooks/useAdminI18n";

export default function AdminBreaking() {
  const { articles, loading, error, updateArticle } = useArticlesDB();
  const { t } = useAdminI18n();
  const [search, setSearch] = useState("");
  const [showOnlyBreaking, setShowOnlyBreaking] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const filteredArticles = useMemo(() => {
    let list = [...articles];
    if (showOnlyBreaking) {
      list = list.filter((a) => a.isBreaking);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          (a.category && a.category.toLowerCase().includes(q)) ||
          (a.author && a.author.toLowerCase().includes(q))
      );
    }
    return list.sort((a, b) => {
      if (a.isBreaking && !b.isBreaking) return -1;
      if (!a.isBreaking && b.isBreaking) return 1;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });
  }, [articles, search, showOnlyBreaking]);

  const activeCount = useMemo(
    () => articles.filter((a) => a.isBreaking).length,
    [articles]
  );

  const handleToggle = async (id: string, current: boolean) => {
    setUpdatingId(id);
    const { error: updateErr } = await updateArticle(id, { isBreaking: !current });
    if (updateErr) {
      // eslint-disable-next-line no-alert
      alert("Fehler beim Aktualisieren: " + updateErr);
    }
    setUpdatingId(null);
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("breaking.title")}</h1>
            <p className="text-sm text-hj-gray mt-0.5">
              {activeCount} {t("breaking.header.subtitle")}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="relative w-full sm:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray"></i>
            <input
              type="text"
              placeholder={t("breaking.search.placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-hj-dark cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyBreaking}
              onChange={(e) => setShowOnlyBreaking(e.target.checked)}
              className="w-4 h-4 rounded border-hj-gray-border text-hj-red focus:ring-hj-red"
            />
            {t("breaking.filter.onlyBreaking")}
          </label>
        </div>

        <div className="bg-white rounded-lg border border-hj-gray-border overflow-hidden">
          <div className="px-5 py-4 border-b border-hj-gray-border flex items-center justify-between">
            <h2 className="text-sm font-bold text-hj-dark">
              {showOnlyBreaking ? "Aktive Breaking News" : "Alle Artikel"}
            </h2>
            <span className="text-xs text-hj-gray">{filteredArticles.length} Artikel</span>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block w-6 h-6 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-hj-gray mt-2">Artikel laden...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-red-500">
              Fehler beim Laden: {error}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="p-8 text-center text-sm text-hj-gray">
              {showOnlyBreaking
                ? t("breaking.table.noBreaking")
                : t("breaking.table.empty")}
            </div>
          ) : (
            <div className="divide-y divide-hj-gray-border">
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  className={`px-5 py-4 flex items-center gap-4 ${
                    article.isBreaking ? "bg-hj-red/[0.02]" : ""
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      article.isBreaking ? "bg-hj-red/10" : "bg-gray-100"
                    }`}
                  >
                    <i
                      className={`text-sm ${
                        article.isBreaking
                          ? "ri-flashlight-line text-hj-red"
                          : "ri-article-line text-gray-400"
                      }`}
                    ></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-hj-dark truncate">
                        {article.title}
                      </p>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                          article.status === "published"
                            ? "bg-green-50 text-green-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {article.status === "published" ? "Live" : "Entwurf"}
                      </span>
                    </div>
                    <p className="text-xs text-hj-gray mt-0.5">
                      {article.category} · {article.author} ·{" "}
                      {new Date(article.publishedAt).toLocaleDateString("de-CH")} ·{" "}
                      {article.readTime}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {article.isBreaking && (
                      <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-hj-red/10 text-hj-red">
                        Breaking
                      </span>
                    )}
                    <button
                      onClick={() => handleToggle(article.id, article.isBreaking)}
                      disabled={updatingId === article.id}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        article.isBreaking ? "bg-hj-red" : "bg-gray-300"
                      } ${updatingId === article.id ? "opacity-60" : ""}`}
                      aria-label={
                        article.isBreaking
                          ? "Breaking News deaktivieren"
                          : "Breaking News aktivieren"
                      }
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          article.isBreaking ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      ></span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}